import React, { useState, useEffect } from 'react';
import '../AssessmentForm.css';
import decodeJWT from '../../../lib/iprs-react-library/src/shared/JWTParser';

/**
 * ConsentTabNetworkEform
 * 
 * Handles consent for Network Eform report types (2010: Initial, 2011: Follow Up)
 * 
 * This component is SEPARATE from ConsentTabTriage because:
 * - Network Eform forms are ADMINISTRATIVE/CLINICAL confirmation forms
 * - They require clinician sign-off (SignedBy field)
 * - They require verification of patient details (DetailsChecked field)
 * - They capture assessment dates (AssessmentDate field)
 * - No patient consent gathering (ConsentGained field) - that's Triage-only
 * - No GP information capture (GpName, GpSurgery, GpAddress) - that's Triage-only
 * 
 * See: Swagger endpoints explicitly support these fields only for types 2010, 2011
 */
const ConsentTabNetworkEform = ({ reportId, api, patientInfo, onSave , isPublished}) => {
  const [caseReference, setCaseReference] = useState('');
  const [fullName, setFullName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [detailsChecked, setDetailsChecked] = useState(false);
  const [signedByClinicianId, setSignedByClinicianId] = useState('');
  const [assessmentDate, setAssessmentDate] = useState('');
  const [clinicians, setClinicians] = useState([]);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingStates, setSavingStates] = useState({});
  const [saveTimeouts, setSaveTimeouts] = useState({});
  const [initialCharCounts, setInitialCharCounts] = useState({});

  useEffect(() => {
    loadConsentData();
  }, [reportId]);

  const loadConsentData = async () => {
    try {
      setLoading(true);

      // Get current user ID from JWT token
      const jwt = decodeJWT(api.getToken());
      const userId = jwt?.UserID || null;
      setCurrentUserId(userId);

      const response = await api.webAPIFetch(`/api/PatientCaseReports/${reportId}/sections/psych-eform-confirmation`);

      if (response.ok) {
        const data = await response.json();
        setCaseReference(data.caseReference || '');
        setFullName(data.fullName || '');
        setBirthDate(data.birthDate || '');
        setDetailsChecked(data.detailsChecked || false);
        setAssessmentDate(data.assessmentDate || '');

        // Parse clinicians array
        setClinicians(data.clinicians || []);

        // Set clinician ID - use saved value, or default to current user
        const clinicianId = data.signedBy || userId || '';
        setSignedByClinicianId(clinicianId);

        // Set loaded fields as 'saved' to show green indicator
        const initialSavingStates = {};
        if (data.detailsChecked !== null) initialSavingStates['DetailsChecked'] = 'saved';
        if (data.assessmentDate) initialSavingStates['AssessmentDate'] = 'saved';
        if (data.signedBy) initialSavingStates['SignedBy'] = 'saved';
        setSavingStates(initialSavingStates);
      }
    } catch (error) {
      console.error('Error loading consent data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveField = async (fieldName, value) => {
    if (isPublished) return;
    setSavingStates(prev => ({ ...prev, [fieldName]: 'saving' }));

    try {
      const response = await api.webAPIFetch(`/api/PatientCaseReports/${reportId}/sections/psych-eform-confirmation/fields/${fieldName}`, {
        method: 'PATCH',
        body: JSON.stringify(value)
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      setSavingStates(prev => ({ ...prev, [fieldName]: 'saved' }));
      if (onSave) onSave();

      // Keep saved indicator - don't clear it
    } catch (error) {
      console.error(`Error saving ${fieldName}:`, error);
      setSavingStates(prev => ({ ...prev, [fieldName]: 'error' }));
    }
  };

  const handleFieldChange = (fieldName, value, setter) => {
    setter(value);

    // Track initial character count if not already tracking
    if (initialCharCounts[fieldName] === undefined) {
      setInitialCharCounts(prev => ({ ...prev, [fieldName]: value.length }));
    }

    // Calculate characters typed since start
    const charsSinceStart = Math.abs(value.length - (initialCharCounts[fieldName] || 0));

    // Clear existing timeout
    if (saveTimeouts[fieldName]) {
      clearTimeout(saveTimeouts[fieldName]);
    }

    // Don't show saving indicator yet - keep previous state
    // Only trigger save if 50+ characters typed
    if (charsSinceStart >= 50) {
      setInitialCharCounts(prev => ({ ...prev, [fieldName]: value.length }));
      saveField(fieldName, value);
      return;
    }

    // Debounce save - 10 seconds after typing stops
    const timeoutId = setTimeout(() => {
      setInitialCharCounts(prev => ({ ...prev, [fieldName]: value.length }));
      saveField(fieldName, value);
    }, 10000); // 10 seconds after typing stops

    setSaveTimeouts(prev => ({ ...prev, [fieldName]: timeoutId }));
  };

  const handleFieldBlur = (fieldName, value) => {
    // Clear debounce timeout on blur
    if (saveTimeouts[fieldName]) {
      clearTimeout(saveTimeouts[fieldName]);
    }

    // Reset character count for next typing session
    setInitialCharCounts(prev => ({ ...prev, [fieldName]: value.length }));

    // Save immediately on blur
    saveField(fieldName, value);
  };

  const getSaveIndicator = (fieldName) => {
    const state = savingStates[fieldName];

    // Check if field has a valid value - don't show indicator for empty fields
    const fieldValues = {
      'DetailsChecked': detailsChecked,
      'AssessmentDate': assessmentDate,
      'SignedBy': signedByClinicianId
    };

    const fieldValue = fieldValues[fieldName];
    const hasValue = fieldValue !== null && fieldValue !== undefined && fieldValue !== '' && fieldValue !== false;

    // Always return a span to allocate space
    if (state === 'saving' && hasValue) {
      return (
        <span className="save-status saving" aria-label="Saving">
          <span className="spinner-small"></span>
        </span>
      );
    }
    if (state === 'saved' && hasValue) {
      return (
        <span className="save-status saved" aria-label="Saved">
          <span className="checkmark">✓</span>
        </span>
      );
    }
    if (state === 'error') {
      return (
        <span className="save-status error" aria-label="Error saving">
          <span className="error-mark">⚠</span>
        </span>
      );
    }
    // Return empty span to maintain space
    return <span className="save-status" aria-hidden="true"></span>;
  };

  const getCharacterCounter = (currentLength, maxLength) => {
    if (!maxLength) return null;
    const remaining = maxLength - currentLength;
    const isNearLimit = remaining <= 50;
    return (
      <span className={`character-counter ${isNearLimit ? 'near-limit' : ''}`}>
        {currentLength} / {maxLength} characters
      </span>
    );
  };

  if (loading) {
    return <div className="loading-state"><div className="spinner"></div><p>Loading confirmation details...</p></div>;
  }

  return (
    <div className="assessment-container">
      <fieldset disabled={isPublished} style={{ border: "none", padding: 0, margin: 0 }}>
      <div className="patient-summary">
        <div><strong>Case ID:</strong> {patientInfo?.caseId || caseReference}</div>
        <div><strong>Name:</strong> {patientInfo?.name || fullName}</div>
        <div><strong>DOB:</strong> {patientInfo?.dateOfBirth || birthDate}</div>
      </div>

      <div className="eform-details-section">
        <h3>Assessment Details Confirmation</h3>
        <p>Please verify that the following information is correct before proceeding:</p>

        <div className="details-display">
          <div className="detail-item">
            <strong>Case Reference:</strong> <span>{caseReference || patientInfo?.caseId}</span>
          </div>
          <div className="detail-item">
            <strong>Patient Name:</strong> <span>{fullName || patientInfo?.name}</span>
          </div>
          <div className="detail-item">
            <strong>Date of Birth:</strong> <span>{birthDate || patientInfo?.dateOfBirth}</span>
          </div>
        </div>

        <div className="form-field details-checked-field">
          <label>
            <input
              type="checkbox"
              checked={detailsChecked}
              onChange={(e) => {
                setDetailsChecked(e.target.checked);
                saveField('DetailsChecked', e.target.checked);
              }}
              required
            />
            <span className="checkbox-label">
              I confirm that the above information is correct for this patient and injury region
            </span>
          </label>
          {getSaveIndicator('DetailsChecked')}
        </div>
      </div>

      <div className="assessment-date-section">
        <div className="form-field">
          <label htmlFor="assessmentDate">
            Assessment Date
            {getSaveIndicator('AssessmentDate')}
          </label>
          <input
            type="date"
            id="assessmentDate"
            value={assessmentDate}
            onChange={(e) => handleFieldChange('AssessmentDate', e.target.value, setAssessmentDate)}
            onBlur={(e) => handleFieldBlur('AssessmentDate', e.target.value)}
            required
          />
        </div>
      </div>

      <div className="clinician-sign-off-section">
        <h3>Clinician Sign-Off</h3>

        <div className="form-field">
          <label htmlFor="signedBy">
            Clinician Name
            {getSaveIndicator('SignedBy')}
          </label>
          <select
            id="signedBy"
            value={signedByClinicianId}
            onChange={(e) => {
              const value = e.target.value;
              setSignedByClinicianId(value);
              // Save immediately on change for dropdown
              if (value) {
                saveField('SignedBy', parseInt(value));
              }
            }}
            required
          >
            <option value="">Select a clinician</option>
            {clinicians.map(clinician => (
              <option key={clinician.id} value={clinician.id}>
                {clinician.fullName}
              </option>
            ))}
          </select>
          {!signedByClinicianId && <div className="validation-error">Clinician selection is required</div>}
        </div>
      </div>
      </fieldset>

    </div>
  );
};

export default ConsentTabNetworkEform;
